import type { IconButtonProps } from '@mui/material/IconButton';

import { toast } from 'sonner';
import { startCase } from 'lodash';
import { usePopover, useBoolean } from 'minimal-shared/hooks';
import { memo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import {
  Card,
  Stack,
  Button,
  Dialog,
  TextField,
  IconButton,
  DialogTitle,
  CardContent,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { fData } from 'src/utils/format-number';

import { sanitizeAndValidate } from 'src/utils';
import { useOrganisation } from 'src/organisation';
import { fetcher, endpoints } from 'src/lib/axios';

import { Iconify } from 'src/components/iconify';
import { UploadAvatar } from 'src/components/upload';
import { CustomPopover } from 'src/components/custom-popover';

import { useMockedUser } from 'src/auth/hooks';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';

// ----------------------------------------------------------------------

export type AccountPopoverProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountPopover({ data = [], sx, ...other }: AccountPopoverProps) {
  const { open, anchorEl, onClose, onOpen } = usePopover();
  const profileDetails = useOrganisation();

  const { user } = useMockedUser();

  const profileDialog = useBoolean();

  const ResetPasswordDialogContent = memo(() => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Iconify
        icon="mingcute:send-plane-fill"
        width={160}
        height={160}
        sx={{ color: 'primary.main' }}
      />
      <Typography variant="h3" sx={{ textAlign: 'center' }}>
        We&apos;ve sent you a link to reset your password.
      </Typography>
      <Typography variant="h6" sx={{ textAlign: 'center' }}>
        Just follow the instructions in the email to set a new one.
      </Typography>
      <Typography variant="subtitle2" color="text.secondary">
        Didn&apos;t receive it? Check your spam folder
      </Typography>
    </Box>
  ));

  const ProfileDialog = ({
    profilePictureUrl,
    displayName,
    email,
    firstName: FirstName,
    lastName: LastName,
    updateProfile,
    updateProfilePicture,
  }: any) => {
    const [inputFields, setInputFields] = useState<{
      firstName: string;
      lastName: string;
    }>({ firstName: '', lastName: '' });
    const [inputErrors, setInputErrors] = useState<{
      firstName: string;
      lastName: string;
    }>({ firstName: '', lastName: '' });
    const resetScreen = useBoolean();
    const [avatarUrl, setAvatarUrl] = useState<File | string | null>(null);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    useEffect(() => {
      setInputFields({
        firstName: FirstName || '',
        lastName: LastName || '',
      });
    }, [FirstName, LastName]);

    useEffect(() => {
      setAvatarUrl(profilePictureUrl || null);
    }, [profilePictureUrl]);

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      field: 'firstName' | 'lastName'
    ) => {
      const value = e.target.value;
      setInputFields({ ...inputFields, [field]: value });
      if (!value.trim() && field === 'firstName') {
        setInputErrors({ ...inputErrors, [field]: `${startCase(field)} is required` });
        return;
      }
      const { isValid, message } = sanitizeAndValidate(
        value,
        'name',
        field === 'lastName' ? false : true
      );
      setInputErrors({ ...inputErrors, [field]: isValid ? '' : message });
    };

    const handleCloseDialog = () => {
      profileDialog.onFalse();
      resetScreen.onFalse();
      setInputErrors({ firstName: '', lastName: '' });
    };

    const handleSaveProfile = () => {
      setIsUpdating(true);
      updateProfile(
        {
          firstName: inputFields.firstName,
          lastName: inputFields.lastName,
        },
        (response: any, success: boolean) => {
          if (success) {
            handleCloseDialog();
          }
          setIsUpdating(false);
        }
      );
    };

    const handleDropAvatar = useCallback(
      (acceptedFiles: File[]) => {
        if (!acceptedFiles.length) return;

        const newFile = acceptedFiles[0];
        const formData = new FormData();
        formData.append('file', newFile);

        updateProfilePicture(formData);
      },
      [updateProfilePicture, setAvatarUrl]
    );

    const handleRemoveAvatar = () => {
      profileDetails?.deleteProfilePicture((response: any, success: boolean) => {
        if (success) {
          setAvatarUrl(null);
        }
      });
    };

    const handleResetPassword = () => {
      fetcher(endpoints.account.resetPassword)
        .then((res) => {
          resetScreen.onTrue();
          setInputErrors({ firstName: '', lastName: '' });
        })
        .catch((err) => {
          toast.error('Failed', {
            description: 'Failed to reset password',
          });
        });
    };

    return (
      <Dialog open={profileDialog.value} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">Profile Settings</Typography>
            <IconButton onClick={handleCloseDialog}>
              <Iconify icon="material-symbols:close-rounded" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {resetScreen.value ? (
            <ResetPasswordDialogContent />
          ) : (
            <Stack direction="row" spacing={2} sx={{ height: '100%', minHeight: 200 }} my={1}>
              <Box sx={{ flex: 1, display: 'flex' }}>
                <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column', maxWidth: '220px' }}
                  >
                    <UploadAvatar
                      value={avatarUrl}
                      onDrop={handleDropAvatar}
                      validator={(fileData) => {
                        if (fileData.size > 2000000) {
                          return {
                            code: 'file-too-large',
                            message: `File is larger than ${fData(2000000)}`,
                          };
                        }
                        return null;
                      }}
                      onDelete={handleRemoveAvatar}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 2,
                        flex: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.primary', ...TextOverflowStyle }}
                      >
                        {displayName || '-'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', ...TextOverflowStyle }}
                      >
                        {email || '-'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: 2, display: 'flex' }}>
                <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                      <TextField
                        label="First Name"
                        value={inputFields.firstName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleChange(e, 'firstName')
                        }
                        required
                        error={!!inputErrors.firstName}
                        helperText={inputErrors.firstName}
                      />
                      <TextField
                        label="Last Name"
                        value={inputFields.lastName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleChange(e, 'lastName')
                        }
                        inputProps={{ maxLength: 50 }}
                        error={!!inputErrors.lastName}
                        helperText={inputErrors.lastName}
                      />
                      <Button
                        variant="soft"
                        color="primary"
                        endIcon={<Iconify icon="material-symbols:lock-reset-outline-rounded" />}
                        onClick={handleResetPassword}
                        sx={{ justifyContent: 'space-between', mt: 'auto' }}
                      >
                        <span>Reset My Password</span>
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {!resetScreen.value && (
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={handleSaveProfile}
              loading={isUpdating}
              size="large"
            >
              Save Changes
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderMenuActions = () => (
    <CustomPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{ paper: { sx: { p: 0, width: 200 } }, arrow: { offset: 20 } }}
    >
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Typography variant="subtitle2" noWrap>
          {profileDetails?.displayName || 'Profile Not Found'}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
          {profileDetails?.email || 'Email Not Found'}
        </Typography>
      </Box>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <MenuList sx={{ p: 1, my: 1, '& li': { p: 0 } }}>
        <MenuItem>
          <Button
            onClick={() => {
              profileDialog.onTrue();
              onClose();
            }}
            startIcon={<Iconify icon="material-symbols:person-outline-rounded" />}
            sx={{ width: '100%', justifyContent: 'flex-start' }}
          >
            Profile
          </Button>
        </MenuItem>
      </MenuList>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <Box sx={{ p: 1 }}>
        <SignOutButton
          size="medium"
          variant="text"
          onClose={onClose}
          sx={{ display: 'block', textAlign: 'left' }}
        />
      </Box>
    </CustomPopover>
  );

  return (
    <>
      <AccountButton
        onClick={onOpen}
        photoURL={profileDetails?.profilePictureUrl || user?.photoURL}
        displayName={profileDetails?.displayName || 'Profile Not Found'}
        sx={sx}
        {...other}
      />

      {renderMenuActions()}
      <ProfileDialog {...profileDetails} />
    </>
  );
}

const TextOverflowStyle = {
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
